/**
 * MobileViewContractModal
 *
 * MUI full-screen Dialog replacement for ViewContractModal (PrimeReact Dialog).
 * Displays contract details with editable fields where allowed.
 * Shows a sticky Save button when changes are detected.
 *
 * Components from MUI: Dialog, AppBar, Toolbar, IconButton, Typography,
 *   TextField, Autocomplete, Box, Slide, Button, Fade
 */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

// MUI Components
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  TextField,
  Autocomplete,
  Box,
  Slide,
  Button,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

// Redux
import {
  contractsLocationsOptions,
  editCollabContract,
  getCollabContract,
  optionsSortie,
  typeOfContractOptions,
} from "../../../components/collabs/collabsSlice";
import { setIsLoading } from "../../common/commonSlice";

// Shared utilities
import Langs, { Localize } from "../../../app/lang/langs";
import { showErrorToast } from "../../../app/utils/helpers";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "12px";
const BORDER_RADIUS_BUTTON = "14px";

const fieldLabelSx = {
  fontWeight: 600,
  mb: 0.75,
  letterSpacing: "-0.01em",
  color: "text.secondary",
  fontSize: "0.82rem",
  textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
  mb: 3,
  "& .MuiOutlinedInput-root": {
    borderRadius: BORDER_RADIUS_INPUT,
    backgroundColor: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(6px)",
    transition: TRANSITION_SMOOTH,
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,0,0.18)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255,144,21,0.5)",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.10)",
    },
  },
  "& input": { fontSize: "16px", transition: TRANSITION_SMOOTH },
  "& textarea": { fontSize: "16px" },
  "& input::placeholder, & textarea::placeholder": { opacity: 0.45 },
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ------------------------------------------------------------------ */
/*  Helper: format date for native input                               */
/* ------------------------------------------------------------------ */
const dateToInput = (date) =>
  date ? moment(date).format("YYYY-MM-DD") : "";

const getFormattedDate = (date) =>
  date ? moment(date).format("YYYY-MM-DD HH:mm:ss") : null;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function MobileViewContractModal(props) {
  const dispatch = useDispatch();
  const companies = useSelector((state) => state.collabs.companies);
  const subcontractors = useSelector((state) => state.clients.subcontractors);
  const allCollabs = useSelector((state) => state.collabs.allCollabsList);

  const [updatedItem, setUpdatedItem] = useState(null);

  // Sync local state when selectedContract changes
  useEffect(() => {
    if (props.selectedContract) {
      setUpdatedItem({ ...props.selectedContract });
    }
  }, [props.selectedContract]);

  const handleClose = () => {
    props.onHide();
  };

  const handleFieldChange = (key, value) => {
    setUpdatedItem((prev) => ({ ...prev, [key]: value }));
  };

  const hasChanges =
    props.selectedContract &&
    updatedItem &&
    JSON.stringify(updatedItem) !== JSON.stringify(props.selectedContract);

  const handleSave = async () => {
    dispatch(setIsLoading(true));
    if (updatedItem && updatedItem.id_collab) {
      const responseContract = await dispatch(
        editCollabContract({
          collab_contract: {
            ...updatedItem,
            sortie_date: getFormattedDate(updatedItem.sortie_date),
            entree_date: getFormattedDate(updatedItem.entree_date),
            date_debut_cp: getFormattedDate(updatedItem.date_debut_cp),
            date_fin_cp: getFormattedDate(updatedItem.date_fin_cp),
            new_date_debut_cp: getFormattedDate(updatedItem.new_date_debut_cp),
          },
        })
      ).unwrap();
      dispatch(getCollabContract({ id_collab: updatedItem.id_collab }));
      if (responseContract.responseData?.error == "conflict") {
        showErrorToast("CONTRACT_CONFLICT_ERROR");
      }
      const contractError =
        responseContract && responseContract["responseError"];
      if (contractError) {
        showErrorToast();
      }
    }
    dispatch(setIsLoading(false));
    if (props.onHide) props.onHide();
  };

  // Determine collab type for conditional field visibility
  const typeCollab = updatedItem ? updatedItem["type_collab"] : null;
  const isInterne = typeCollab == "interne";

  /* ---------------------------------------------------------------- */
  /*  Field definitions — mirrors desktop ViewContractModal exactly    */
  /* ---------------------------------------------------------------- */
  const getFields = () => {
    if (!updatedItem) return [];
    return [
      // --- Always visible ---
      { key: "type_collab", label: "TYPE_COLLAB", kind: "text", disabled: true },
      {
        key: "id_company",
        label: "COMPANY",
        kind: "dropdown",
        options: companies,
        disabled: true,
      },
      {
        key: "type_contrat",
        label: "CONTRACT_TYPE",
        kind: "dropdown",
        options: typeOfContractOptions,
        disabled: true,
      },
      // --- Externe only ---
      {
        key: "id_subcontractor",
        label: "SUBCONTRACTOR",
        kind: "dropdown",
        options: subcontractors,
        forType: "externe",
      },
      // --- Interne only ---
      {
        key: "statut_emploi",
        label: "STATUT_EMPLOI",
        kind: "text",
        forType: "interne",
        disabled: true,
      },
      // --- Mixed ---
      {
        key: "etablissement",
        label: "ETABLISSEMENT",
        kind: "dropdown",
        options: contractsLocationsOptions,
        disabled: isInterne,
      },
      {
        key: "entree_date",
        label: "START_DATE",
        kind: "date",
        disabled: isInterne,
      },
      { key: "sortie_date", label: "END_DATE", kind: "date" },
      {
        key: "motif_sortie",
        label: "MOTIF_SORTIE",
        kind: "dropdown",
        options: optionsSortie,
      },
      // --- Externe only ---
      {
        key: "date_debut_cp",
        label: "DATE_DEBUT_CP",
        kind: "date",
        forType: "externe",
      },
      {
        key: "date_fin_cp",
        label: "DATE_FIN_CP",
        kind: "date",
        forType: "externe",
      },
      // --- Interne only ---
      {
        key: "code_csp",
        label: "CODE_CSP",
        kind: "text",
        forType: "interne",
        disabled: true,
      },
      {
        key: "coefficient",
        label: "COEFFICIENT",
        kind: "text",
        forType: "interne",
        disabled: true,
      },
      {
        key: "position",
        label: "POSITION",
        kind: "text",
        forType: "interne",
        disabled: true,
      },
      {
        key: "periode_essai_1",
        label: "PERIODE_ESSAI_1",
        kind: "date",
        forType: "interne",
        disabled: true,
      },
      {
        key: "periode_essai_2",
        label: "PERIODE_ESSAI_2",
        kind: "date",
        forType: "interne",
        disabled: true,
      },
      // --- Externe only ---
      {
        key: "tjm_achat",
        label: "TJM_ACHAT",
        kind: "text",
        forType: "externe",
      },
      {
        key: "tjm_vente",
        label: "TJM_VENTE",
        kind: "text",
        forType: "externe",
      },
      // --- Interne only ---
      {
        key: "ticket_restaurant",
        label: "TICKET_RESTAURANT",
        kind: "text",
        forType: "interne",
        disabled: true,
      },
      // --- Always visible ---
      {
        key: "did_not_start",
        label: "DID_NOT_START",
        kind: "text",
        disabled: true,
      },
      // --- Interne only ---
      {
        key: "dedit_formation",
        label: "DEDIT_FORMATION",
        kind: "text",
        forType: "interne",
      },
      {
        key: "school",
        label: "SCHOOL",
        kind: "text",
        forType: "interne",
      },
      {
        key: "school_contact",
        label: "SCHOOL_CONTACT",
        kind: "text",
        forType: "interne",
      },
      {
        key: "diploma",
        label: "DIPLOMA",
        kind: "text",
        forType: "interne",
      },
      {
        key: "training_cost",
        label: "TRAINING_COST",
        kind: "text",
        forType: "interne",
      },
      {
        key: "remaining_cost",
        label: "REMAINING_COST",
        kind: "text",
        forType: "interne",
      },
      {
        key: "id_tutor",
        label: "TUTOR",
        kind: "dropdown",
        options: allCollabs,
        forType: "interne",
      },
      // --- Always visible ---
      { key: "comments", label: "COMMENTS", kind: "multiline" },
    ];
  };

  /* ---------------------------------------------------------------- */
  /*  Render helpers                                                   */
  /* ---------------------------------------------------------------- */
  const renderTextField = (field) => (
    <TextField
      key={field.key}
      size="small"
      fullWidth
      disabled={!!field.disabled}
      value={updatedItem[field.key] || ""}
      onChange={(e) => handleFieldChange(field.key, e.target.value)}
      sx={glassmorphicTextFieldSx}
    />
  );

  const renderMultilineField = (field) => (
    <TextField
      key={field.key}
      size="small"
      fullWidth
      multiline
      minRows={3}
      disabled={!!field.disabled}
      value={updatedItem[field.key] || ""}
      onChange={(e) => handleFieldChange(field.key, e.target.value)}
      sx={glassmorphicTextFieldSx}
    />
  );

  const renderDateField = (field) => (
    <TextField
      key={field.key}
      type="date"
      size="small"
      fullWidth
      disabled={!!field.disabled}
      value={dateToInput(updatedItem[field.key])}
      onChange={(e) =>
        handleFieldChange(
          field.key,
          e.target.value ? new Date(e.target.value) : null
        )
      }
      InputLabelProps={{ shrink: true }}
      sx={glassmorphicTextFieldSx}
    />
  );

  const renderDropdownField = (field) => {
    const options = field.options || [];
    const currentValue = updatedItem[field.key];
    const selectedOption =
      options.find((o) => o.value === currentValue) || null;

    return (
      <Autocomplete
        key={field.key}
        options={options}
        getOptionLabel={(opt) => opt.label || ""}
        isOptionEqualToValue={(opt, val) => opt.value === val.value}
        value={selectedOption}
        disabled={!!field.disabled}
        onChange={(_, newVal) =>
          handleFieldChange(field.key, newVal ? newVal.value : null)
        }
        slotProps={{ popper: { sx: { zIndex: 1500 } } }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={Localize(field.label)}
            size="small"
            sx={glassmorphicTextFieldSx}
          />
        )}
        fullWidth
      />
    );
  };

  const renderField = (field) => {
    switch (field.kind) {
      case "text":
        return renderTextField(field);
      case "multiline":
        return renderMultilineField(field);
      case "date":
        return renderDateField(field);
      case "dropdown":
        return renderDropdownField(field);
      default:
        return renderTextField(field);
    }
  };

  return (
    <Dialog
      fullScreen
      open={!!props.selectedContract}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{
        zIndex: 1400,
        "& .MuiDialog-paper": {
          background: "var(--surface-ground, #f4f6f8)",
        },
      }}
    >
      {/* Top AppBar */}
      <AppBar
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "rgba(255,255,255,0.82)",
          color: "text.primary",
          boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Toolbar sx={{ py: 0.5 }}>
          <IconButton
            edge="start"
            onClick={handleClose}
            aria-label="close"
            sx={{
              transition: TRANSITION_SMOOTH,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.06)",
                transform: "scale(1.08)",
              },
              "&:active": { transform: "scale(0.95)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              ml: 1.5,
              flex: 1,
              fontWeight: 600,
              fontSize: "1.15rem",
              letterSpacing: "-0.02em",
            }}
            variant="h6"
          >
            <Langs str="VIEW_CONTRACT" />
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Form Content */}
      <Box
        sx={{
          p: 2.5,
          pb: hasChanges ? 14 : 4,
          overflowY: "auto",
          flex: 1,
          "@keyframes fadeSlideIn": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          animation: "fadeSlideIn 0.35s cubic-bezier(0, 0, 0.2, 1) both",
        }}
      >
        <Box
          sx={{
            background: "rgba(255,255,255,0.65)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.45)",
            boxShadow:
              "0 2px 16px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
            p: 2.5,
            transition: TRANSITION_SMOOTH,
          }}
        >
          {updatedItem &&
            getFields()
              .filter(
                (field) =>
                  !field.forType || field.forType === typeCollab
              )
              .map((field) => (
                <React.Fragment key={field.key}>
                  <Typography variant="body2" sx={fieldLabelSx}>
                    <Langs str={field.label} />
                  </Typography>
                  {renderField(field)}
                </React.Fragment>
              ))}
        </Box>
      </Box>

      {/* Sticky bottom Save button — only visible when changes detected */}
      <Fade in={!!hasChanges}>
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            p: 2.5,
            bgcolor: "rgba(255,255,255,0.78)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            boxShadow: "0 -4px 24px rgba(0,0,0,0.06)",
            borderTop: "1px solid rgba(255,255,255,0.5)",
            zIndex: 1,
          }}
        >
          <Button
            variant="contained"
            color="warning"
            fullWidth
            onClick={handleSave}
            startIcon={<SaveIcon />}
            sx={{
              minHeight: 52,
              borderRadius: BORDER_RADIUS_BUTTON,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1.05rem",
              letterSpacing: "-0.01em",
              boxShadow: "0 4px 14px rgba(255,144,21,0.28)",
              transition: TRANSITION_SMOOTH,
              "&:hover": {
                transform: "scale(1.02)",
                boxShadow: "0 6px 20px rgba(255,144,21,0.35)",
              },
              "&:active": {
                transform: "scale(0.98)",
              },
            }}
          >
            <Langs str="SAVE" />
          </Button>
        </Box>
      </Fade>
    </Dialog>
  );
}
